import React, { useEffect, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tag, Plus, Pencil, Trash2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import TagFormModal from './TagFormModal';

const TagsPage = () => {
  const [tags, setTags]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTag, setEditTag]     = useState(null);   // null = create mode

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      const res = await api.get('/tags');
      setTags(res.data || []);
    } catch {
      toast.error('Kunde inte hämta taggar');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditTag(null);
    setModalOpen(true);
  };

  const openEdit = (tag) => {
    setEditTag(tag);
    setModalOpen(true);
  };

  const handleDelete = async (tag) => {
    if (!window.confirm(
      `Är du säker på att du vill ta bort taggen "${tag.name}"? Detta går inte att ångra.`
    )) return;
    try {
      await api.delete(`/tags/${tag.id}`);
      toast.success('Tagg borttagen!');
      fetchTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde inte ta bort taggen');
    }
  };

  const existingNames = tags.map(t => t.name);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="tags-page">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-black tracking-tight">Taggar</h1>
            <p className="text-muted-foreground mt-1">
              Hantera taggar för att kategorisera och filtrera innehåll
            </p>
          </div>
          <Button
            data-testid="create-tag-btn"
            onClick={openCreate}
            className="rounded-full font-bold"
            style={{ background: '#F9CF3F', color: '#333333' }}
          >
            <Plus size={18} className="mr-2" /> Ny tagg
          </Button>
        </div>

        {/* Tag list */}
        {loading ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              Laddar taggar...
            </CardContent>
          </Card>
        ) : tags.length === 0 ? (
          <Card data-testid="empty-state">
            <CardContent className="p-12 text-center">
              <Tag size={48} className="mx-auto mb-4 text-muted-foreground/20" />
              <p className="text-muted-foreground text-lg mb-4">
                Inga taggar skapade än. Skapa din första tagg.
              </p>
              <Button
                onClick={openCreate}
                className="rounded-full font-bold"
                style={{ background: '#F9CF3F', color: '#333333' }}
              >
                <Plus size={16} className="mr-2" /> Skapa tagg
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tags-list">
            {tags.map(tag => (
              <Card
                key={tag.id}
                data-testid={`tag-card-${tag.id}`}
                className="hover:shadow-md transition-shadow"
              >
                <CardContent className="p-4 flex items-center gap-3">
                  {/* Color dot + name */}
                  <span
                    className="inline-flex items-center gap-1.5 flex-1 min-w-0"
                    data-testid={`tag-display-${tag.id}`}
                  >
                    <span
                      className="w-3 h-3 rounded-full shrink-0"
                      style={{ background: tag.color }}
                      aria-hidden="true"
                    />
                    <span
                      className="font-bold truncate"
                      style={{ color: '#333333' }}
                      data-testid={`tag-name-${tag.id}`}
                    >
                      {tag.name}
                    </span>
                  </span>

                  {/* Category badge */}
                  <span
                    className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium shrink-0"
                    data-testid={`tag-category-${tag.id}`}
                  >
                    {tag.category}
                  </span>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      data-testid={`edit-tag-${tag.id}`}
                      onClick={() => openEdit(tag)}
                      aria-label={`Redigera ${tag.name}`}
                      className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      data-testid={`delete-tag-${tag.id}`}
                      onClick={() => handleDelete(tag)}
                      aria-label={`Ta bort ${tag.name}`}
                      className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Modal */}
        <TagFormModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSaved={fetchTags}
          editTag={editTag}
          existingNames={existingNames}
        />
      </div>
    </DashboardLayout>
  );
};

export default TagsPage;
